import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useGetList, useGetOne, useRecordContext } from "ra-core";
import type { CompanyWithDoor } from "./types";

/**
 * What has this door already been sent?
 *
 * The question Zoho could not answer, because it split campaign membership,
 * per-touch state and mail history across three unrelated fields.
 *
 * Reads `mailings` directly rather than the `company_mail_history` view: the
 * view LEFT JOINs mailings, so a promoted door with no mail yet yields a row
 * whose mailing_id is null, and react-admin requires a non-null id per record.
 * The view remains the right shape for SQL and reporting.
 */

/** One row per physical door. Mirrors public.addresses. */
type Address = {
  id: number;
  street: string;
  city: string | null;
  state: string | null;
  zip5: string;
  mail_to: string | null;
  cell: string | null;
  is_mailable: boolean;
};

/** One row per door per touch. Mirrors public.mailings. */
type Mailing = {
  id: number;
  address_id: number;
  touch: string;
  cell: string;
  mail_date: string | null;
  cost_cents: number | null;
  vendor: string | null;
  status: string;
  is_test: boolean;
};

/** Delivered is the only outcome worth celebrating; the two failure states
 *  need to be visually distinct from "we simply have not sent it yet". */
const STATUS_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  delivered: "default",
  sent: "secondary",
  planned: "outline",
  cancelled: "outline",
  undeliverable: "destructive",
  returned: "destructive",
};

const formatCents = (cents: number | null) =>
  cents == null ? "—" : `$${(cents / 100).toFixed(2)}`;

export const MailHistory = () => {
  const company = useRecordContext<CompanyWithDoor>();
  const addressId = company?.address_id ?? null;

  const { data: address, isPending: addressPending } = useGetOne<Address>(
    "addresses",
    { id: addressId as number },
    { enabled: addressId != null },
  );

  const { data: mailings, isPending: mailingsPending } = useGetList<Mailing>(
    "mailings",
    {
      filter: { address_id: addressId },
      sort: { field: "touch", order: "ASC" },
      pagination: { page: 1, perPage: 100 },
    },
    { enabled: addressId != null },
  );

  if (!company) return null;

  // Not every company comes from the mailing list. A lead from a phone call or
  // a referral has no door, and that is a normal state, not an error.
  if (addressId == null) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-muted-foreground">
          Not linked to a mailing address.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Companies promoted from the mailing list show every postcard sent to
          their door here.
        </p>
      </div>
    );
  }

  if (addressPending || mailingsPending) {
    return (
      <div className="py-4 space-y-2">
        <Skeleton className="h-5 w-64" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const rows = mailings ?? [];
  const realSpend = rows
    .filter((m) => !m.is_test)
    .reduce((sum, m) => sum + (m.cost_cents ?? 0), 0);

  return (
    <div className="pt-2 space-y-4">
      {address && (
        <div className="text-sm">
          <div className="font-medium">{address.mail_to ?? "—"}</div>
          <div className="text-muted-foreground">
            {address.street}
            {address.city ? `, ${address.city}` : ""}
            {address.state ? `, ${address.state}` : ""} {address.zip5}
          </div>
          <div className="flex gap-2 mt-2">
            {address.cell && (
              <Badge variant="outline">Cell {address.cell}</Badge>
            )}
            {/* A door drops off the mailable list when a merge collapses two
                spellings, or when it is suppressed. Worth surfacing: it
                explains why no further mail will arrive. */}
            {!address.is_mailable && (
              <Badge variant="destructive">Retired — no longer mailed</Badge>
            )}
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">
          Nothing mailed to this door yet.
        </p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Touch</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead className="text-right">Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="font-medium">
                    {m.touch}
                    {m.is_test && (
                      <Badge variant="outline" className="ml-2">
                        test
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{m.mail_date ?? "—"}</TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[m.status] ?? "outline"}>
                      {m.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="uppercase text-xs text-muted-foreground">
                    {m.vendor ?? "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCents(m.cost_cents)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {/* Test sends are excluded: they cost nothing and counting them would
              overstate spend on a door. */}
          <div className="text-xs text-muted-foreground text-right">
            {rows.filter((m) => !m.is_test).length} real touch
            {rows.filter((m) => !m.is_test).length === 1 ? "" : "es"} ·{" "}
            {formatCents(realSpend)} spent
          </div>
        </>
      )}
    </div>
  );
};
